import { useCallback } from "react";
import { logConnectorFailure, logEvent } from "../../../../../telemetry";
import { useDocuparserClient } from "../../../hooks/useDocuparserClient";
import { useUploadReducer } from "./useUploadReducer";
import { uploadToPresignedUrl } from "../../../services/presignedUpload";
import { UPLOAD_ERROR_MESSAGES } from "../../../constants";

/**
 * Client-data/mutation hook for the upload page — see
 * useDocuparserDocuments.ts's identical note on the one-hook-per-concern
 * convention this mirrors from MyQueue.
 */
export function useDocuparserUpload() {
  const client = useDocuparserClient();
  const [uiState, dispatch] = useUploadReducer();

  const submit = useCallback(
    async (file: File, name: string, description: string) => {
      dispatch({ type: "RESET_PROGRESS" });
      dispatch({ type: "START_UPLOAD" });

      try {
        const initiateResponse = await client.initiateUpload(file.name);
        if (!initiateResponse.success || !initiateResponse.data) {
          throw new Error(initiateResponse.error ?? UPLOAD_ERROR_MESSAGES.INITIATE_FAILED);
        }

        const { presignedUrl, inputBlobPath } = initiateResponse.data;
        if (!presignedUrl) throw new Error(UPLOAD_ERROR_MESSAGES.MISSING_PRESIGNED_URL);
        if (!inputBlobPath) throw new Error(UPLOAD_ERROR_MESSAGES.MISSING_BLOB_PATH);

        await uploadToPresignedUrl(presignedUrl, file, (progress) => {
          dispatch({ type: "SET_PROGRESS", payload: progress });
        });

        const completeResponse = await client.completeUpload(file.name, name, description, inputBlobPath);
        if (!completeResponse.success) {
          throw new Error(completeResponse.error ?? UPLOAD_ERROR_MESSAGES.COMPLETE_FAILED);
        }

        logEvent("connector.upload", "usage", { connectorSystemName: "docuparser", fileType: "pdf" });
        dispatch({ type: "UPLOAD_SUCCESS" });
      } catch (error: unknown) {
        dispatch({ type: "RESET_PROGRESS" });
        const errorMsg = error instanceof Error ? error.message : UPLOAD_ERROR_MESSAGES.UPLOAD_FAILED_GENERIC;
        logConnectorFailure("docuparser", error, "upload-failed");
        dispatch({ type: "UPLOAD_ERROR", payload: errorMsg });
      }
    },
    [client, dispatch],
  );

  const setDragging = useCallback((dragging: boolean) => dispatch({ type: "SET_DRAGGING", payload: dragging }), [
    dispatch,
  ]);
  const resetProgress = useCallback(() => dispatch({ type: "RESET_PROGRESS" }), [dispatch]);
  const closeSuccessPopup = useCallback(() => dispatch({ type: "CLOSE_SUCCESS_POPUP" }), [dispatch]);
  const closeErrorPopup = useCallback(() => dispatch({ type: "CLOSE_ERROR_POPUP" }), [dispatch]);

  return { uiState, submit, setDragging, resetProgress, closeSuccessPopup, closeErrorPopup };
}
